import React, { useState, useEffect } from 'react'
import { T, pct, probColor, SearchBar, searchMatch } from '../ui'
import { getFutures, FUTURES_STATS, getTeamFutures, getNFLSeason } from '../api'

const SEASON_POS = [['', 'All'], ['QB', 'QB'], ['RB', 'RB'], ['WR', 'WR'], ['TE', 'TE']]

// Season-long stat → the per-game market its intel/trend read maps to.
const STAT_MARKET = { pass_yards: 'Pass Yds', rush_yards: 'Rush Yds', rec_yards: 'Rec Yds',
  receptions: 'Receptions', pass_tds: 'Pass TDs', rush_tds: 'Anytime TD', rec_tds: 'Anytime TD' }
// Compact season-case verdict chip.
function CaseChip({ c }) {
  if (!c) return null
  const strong = /strong over|leans over/i.test(c.verdict)
  const under = /under/i.test(c.verdict)
  const col = strong ? T.green : under ? T.red : T.amber
  return (
    <span style={{ fontSize: 10.5, fontWeight: 800, color: col, background: strong ? T.greenDim : 'transparent',
      border: `1px solid ${col}`, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>
      {c.greens}✓{c.reds > 0 ? ` ${c.reds}✕` : ''} · {c.verdict}
    </span>
  )
}

/**
 * Futures — season-long player predictions. Pick a stat → ranked projected
 * leaders, each with a floor/ceiling range and P(over) for the milestone lines.
 * Powered by the season projector (/nfl/futures).
 */
export default function FuturesTab({ season, onPlayer }) {
  const [mode, setMode] = useState('players')       // players | proj | teams
  const [stat, setStat] = useState('pass_yards')
  const [pos, setPos] = useState('')                // Projections position filter
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')

  useEffect(() => {
    let live = true
    setLoading(true)
    const req = mode === 'teams' ? getTeamFutures(season)
      : mode === 'proj' ? getNFLSeason(season, pos, stat)
      : getFutures(season, stat)
    req.then(d => { if (live) { setData(d); setLoading(false) } })
    return () => { live = false }
  }, [season, stat, mode, pos])

  const leaders = (data?.leaders || []).filter(searchMatch(q, p => [p.name, p.team, p.position]))
  const teams = (data?.teams || []).filter(searchMatch(q, t => [t.team, t.division]))
  const sample = data?._sample

  return (
    <div style={{ padding: '14px 12px 90px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Season Futures</div>
        <div style={{ fontSize: 11, color: T.muted }}>{season} · projected</div>
      </div>

      {/* players / teams toggle */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 11, background: T.card2, borderRadius: 10, padding: 3 }}>
        {[['players', 'Leaders'], ['proj', 'Projections'], ['teams', 'Teams']].map(([k, lbl]) => (
          <button key={k} onClick={() => setMode(k)}
            style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: mode === k ? T.green : 'transparent', color: mode === k ? '#04140C' : T.muted,
              fontSize: 12.5, fontWeight: 800 }}>{lbl}</button>
        ))}
      </div>

      {/* position filter (Projections only) */}
      {mode === 'proj' && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {SEASON_POS.map(([k, lbl]) => {
            const on = k === pos
            return (
              <button key={k || 'all'} onClick={() => setPos(k)}
                style={{ flex: 1, padding: '7px 0', borderRadius: 20, cursor: 'pointer',
                  border: `1px solid ${on ? T.green : T.border}`, background: on ? T.green : T.card,
                  color: on ? '#04140D' : T.muted, fontSize: 12, fontWeight: 800 }}>{lbl}</button>
            )
          })}
        </div>
      )}

      {/* stat selector (Leaders + Projections) */}
      {(mode === 'players' || mode === 'proj') && (
        <div style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 8, marginBottom: 4 }}>
          {FUTURES_STATS.map(s => {
            const on = s.key === stat
            return (
              <button key={s.key} onClick={() => setStat(s.key)}
                style={{ flex: '0 0 auto', padding: '7px 13px', borderRadius: 20, cursor: 'pointer',
                  border: `1px solid ${on ? T.green : T.border}`, background: on ? T.green : 'transparent',
                  color: on ? '#04140C' : T.muted, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {s.label}
              </button>
            )
          })}
        </div>
      )}

      {sample && (
        <div style={{ fontSize: 11, color: T.amber, background: '#2A210A', border: `1px solid #6B4E0A`,
          borderRadius: 8, padding: '7px 10px', marginBottom: 10 }}>
          Live futures unavailable right now — check back shortly.
        </div>
      )}

      {!loading && !sample && <SearchBar value={q} onChange={setQ}
        placeholder={mode === 'teams' ? 'Search team or division…' : 'Search player or team…'} />}

      {loading && <div style={{ color: T.muted, fontSize: 13, padding: 20, textAlign: 'center' }}>Loading…</div>}

      {/* TEAMS view — win totals, division & Super Bowl odds */}
      {!loading && mode === 'teams' && teams.map((t, i) => (
        <div key={t.team} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
          padding: '12px 13px', marginBottom: 9 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: T.green }}>#{i + 1}</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{t.team}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{t.division} · {t.wins}-{t.losses} · {t.margin_pg > 0 ? '+' : ''}{t.margin_pg}/g
                  {t.rated_from && t.rated_from !== season && <span style={{ color: T.faint }}> ({t.rated_from} form)</span>}</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>{t.proj_win_total}</div>
              <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.08em' }}>PROJ WINS</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
            {[['Win o' + t.win_total_line, t.p_over, true], ['Division', t.division_odds, false], ['Super Bowl', t.sb_odds, false]].map(([lbl, v, isOU]) => (
              <div key={lbl} style={{ flex: 1, textAlign: 'center', background: T.card2, borderRadius: 8, padding: '6px 4px' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: isOU ? probColor(v) : T.text }}>{pct(v)}</div>
                <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{lbl}</div>
              </div>
            ))}
          </div>

          {/* book comparison — appears once season futures odds are pulled. Edge =
              model − book (green = model sees more value than the book prices in). */}
          {(t.book_sb != null || t.book_division != null) && (
            <div style={{ display: 'flex', gap: 14, marginTop: 8, paddingTop: 8,
              borderTop: `1px solid ${T.border}`, fontSize: 11 }}>
              <span style={{ color: T.muted, fontWeight: 700, letterSpacing: '0.04em' }}>BOOK</span>
              {t.book_division != null && (
                <span style={{ color: T.muted }}>Div <b style={{ color: T.text }}>{pct(t.book_division)}</b>
                  {t.div_edge != null && <span style={{ color: t.div_edge >= 0 ? T.green : T.red, marginLeft: 4 }}>
                    {t.div_edge >= 0 ? '+' : ''}{(t.div_edge * 100).toFixed(1)}</span>}
                </span>
              )}
              {t.book_sb != null && (
                <span style={{ color: T.muted }}>SB <b style={{ color: T.text }}>{pct(t.book_sb)}</b>
                  {t.sb_edge != null && <span style={{ color: t.sb_edge >= 0 ? T.green : T.red, marginLeft: 4 }}>
                    {t.sb_edge >= 0 ? '+' : ''}{(t.sb_edge * 100).toFixed(1)}</span>}
                </span>
              )}
            </div>
          )}
        </div>
      ))}

      {!loading && mode === 'players' && leaders.map(p => (
        <div key={p.gsis_id} onClick={() => onPlayer && onPlayer({ gsis_id: p.gsis_id, full_name: p.name,
            pos: p.position, team: p.team, note: `Season projection · ${p.per_game}/g over ${p.games} g`,
            case_future: p.case, market: STAT_MARKET[stat] || 'Rec Yds',
            line: p.case ? Math.round((p.case.line / 17) * 10) / 10 : 0 })}
          style={{ background: T.card, border: `1px solid ${p.case && /strong over/i.test(p.case.verdict) ? T.greenMid : T.border}`, borderRadius: 12,
            padding: '12px 13px', marginBottom: 9, cursor: onPlayer ? 'pointer' : 'default' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: T.green }}>#{p.rank}</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{p.name}
                  <span style={{ fontSize: 11, color: T.muted, marginLeft: 6 }}>{p.position} · {p.team}</span>
                </div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{p.per_game}/g · {p.games} g expected</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>{Math.round(p.projected).toLocaleString()}</div>
              <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.08em' }}>EXPECTED</div>
              {p.full_pace ? <div style={{ fontSize: 10.5, color: T.faint, marginTop: 3 }}>{Math.round(p.full_pace).toLocaleString()} <span style={{ letterSpacing: '0.04em' }}>@ 17 g</span></div> : null}
            </div>
          </div>

          {/* floor–ceiling range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '9px 0 4px', fontSize: 10.5, color: T.muted }}>
            <span>{Math.round(p.floor).toLocaleString()}</span>
            <div style={{ flex: 1, height: 4, background: T.card2, borderRadius: 3, position: 'relative' }}>
              <div style={{ position: 'absolute', left: '25%', right: '25%', top: 0, bottom: 0, background: T.green, opacity: 0.5, borderRadius: 3 }} />
            </div>
            <span>{Math.round(p.ceiling).toLocaleString()}</span>
          </div>

          {/* milestone over-probabilities */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {p.milestones.map(mi => (
              <div key={mi.line} style={{ flex: 1, minWidth: 62, textAlign: 'center', background: T.card2,
                borderRadius: 8, padding: '6px 4px' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: probColor(mi.prob_over) }}>{pct(mi.prob_over)}</div>
                <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>o{mi.line.toLocaleString()}</div>
              </div>
            ))}
          </div>

          {/* season-long case verdict — tap the card for the full breakdown */}
          {p.case && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              marginTop: 9, paddingTop: 9, borderTop: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 11, color: T.muted }}>The Case <span style={{ color: T.faint }}>· tap for why</span></span>
              <CaseChip c={p.case} />
            </div>
          )}
        </div>
      ))}

      {/* PROJECTIONS view — per-player season stat line, position-filterable */}
      {!loading && mode === 'proj' && (data?.players || [])
        .filter(searchMatch(q, p => [p.name, p.team, p.position]))
        .map((p, i) => {
          const st = (p.stats && p.stats[stat]) || {}
          const statLabel = (FUTURES_STATS.find(s => s.key === stat) || {}).label || stat
          return (
            <div key={p.gsis_id} onClick={() => onPlayer && onPlayer({ gsis_id: p.gsis_id, full_name: p.name, pos: p.position, team: p.team })}
              style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '11px 13px',
                marginBottom: 8, cursor: onPlayer ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: T.green }}>#{i + 1}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}
                    <span style={{ fontSize: 11, color: T.muted, marginLeft: 6 }}>{p.position} · {p.team}</span></div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>
                    {st.per_game != null ? `${st.per_game}/g` : '—'}{p.games ? ` · ${p.games} g` : ''}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 19, fontWeight: 800, color: T.text }}>{st.total != null ? Math.round(st.total).toLocaleString() : '—'}</div>
                <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.06em' }}>PROJ {statLabel.toUpperCase()}</div>
                {st.floor != null && st.ceiling != null && (
                  <div style={{ fontSize: 10.5, color: T.faint, marginTop: 2 }}>{Math.round(st.floor).toLocaleString()}–{Math.round(st.ceiling).toLocaleString()}</div>)}
              </div>
            </div>
          )
        })}

      {!loading && ((mode === 'players' && !leaders.length) || (mode === 'teams' && !teams.length) || (mode === 'proj' && !(data?.players || []).length)) && (
        <div style={{ color: T.muted, fontSize: 13, padding: 24, textAlign: 'center' }}>
          {q.trim() ? `No ${mode === 'teams' ? 'teams' : 'players'} match "${q}".`
            : `No season projections yet — they populate once the season projector has run. Check back soon.`}
        </div>
      )}
    </div>
  )
}
